import type {
  DistributionData,
  PriceMode,
  TranslationFunction,
} from './DistributionSection.types'
import { formatPrice } from './DistributionSection.utils'

export const getDistributionExports = (
  data: DistributionData | null,
  isRental: boolean,
  priceMode: PriceMode | undefined,
  t: TranslationFunction
) => ({
  getKPIs: () => {
    if (!data) return []
    return [
      {
        id: 'total-listings',
        label: t('distribution.kpis.totalListings'),
        value: data.stats.rooms.total.toLocaleString(),
        variant: 'default',
      },
      {
        id: 'median-rooms',
        label: t('distribution.kpis.medianRooms'),
        value: data.stats.rooms.median.toFixed(1),
        variant: 'default',
      },
      {
        id: 'median-price',
        label: isRental
          ? t('distribution.kpis.medianRent')
          : t('distribution.kpis.medianPrice'),
        value: `${formatPrice(data.stats.price.median, isRental, priceMode)} CHF`,
        variant: 'default',
      },
      {
        id: 'median-area',
        label: t('distribution.kpis.medianArea'),
        value: `${data.stats.area.median.toFixed(0)} m²`,
        variant: 'default',
      },
    ]
  },
  getInsights: () => {
    if (!data) return []
    const insights = []

    // Most common room count
    const mostCommonRooms = data.rooms.reduce(
      (max, curr) => (curr.count > max.count ? curr : max),
      data.rooms[0]
    )
    if (mostCommonRooms) {
      insights.push({
        title: t('distribution.insights.mostCommonRooms'),
        description: t('distribution.insights.mostCommonRoomsDesc', {
          rooms: mostCommonRooms.label,
          percentage: mostCommonRooms.percentage.toFixed(1),
        }),
      })
    }

    // Price range insight
    if (data.stats.price.avg > 0) {
      insights.push({
        title: isRental
          ? t('distribution.insights.rentRange')
          : t('distribution.insights.priceRange'),
        description: t('distribution.insights.priceRangeDesc', {
          avg: formatPrice(data.stats.price.avg, isRental, priceMode),
          median: formatPrice(data.stats.price.median, isRental, priceMode),
        }),
      })
    }

    // Area insight
    if (data.stats.area.avg > 0) {
      insights.push({
        title: t('distribution.insights.areaRange'),
        description: t('distribution.insights.areaRangeDesc', {
          avg: data.stats.area.avg.toFixed(0),
          median: data.stats.area.median.toFixed(0),
        }),
      })
    }

    return insights
  },
})
